// The landing page's interactive generator: Image-to-Video / Text-to-Video
// tabs, a local-only image picker, a prompt box and a duration slider.
// Deliberately makes ZERO network calls — /upload and /pricing/quote are
// auth-gated, so the picked File stays client-side (object-URL preview) and
// is handed to the generation page via lib/landingDraft after the visitor
// becomes a user. Clicking Generate stashes the draft and opens the signup
// nudge (landing variant).

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeRounded';
import { format, useTranslation } from '../../i18n/I18nProvider';
import { setDraft, type LandingTab } from '../../lib/landingDraft';

export default function HeroGenerator({ onGenerate }: { onGenerate: () => void }) {
  const t = useTranslation();
  const [tab, setTab] = useState<LandingTab>('image-to-video');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [hint, setHint] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pickFile = (f: File | undefined | null) => {
    if (!f) return;
    setFile(f);
    setHint(null);
  };

  const handleGenerate = () => {
    if (!prompt.trim()) {
      setHint(t.landing.promptRequired);
      return;
    }
    if (tab === 'image-to-video' && !file) {
      setHint(t.landing.imageRequired);
      return;
    }
    setHint(null);
    setDraft({ prompt: prompt.trim(), file: tab === 'image-to-video' ? file : null, tab });
    onGenerate();
  };

  return (
    <Paper elevation={6} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3, textAlign: 'left' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v as LandingTab);
          setHint(null);
        }}
        sx={{ mb: 2.5 }}
      >
        <Tab value="image-to-video" label={t.landing.tabImageToVideo} />
        <Tab value="text-to-video" label={t.landing.tabTextToVideo} />
      </Tabs>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
        {tab === 'image-to-video' && (
          <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
            {preview ? (
              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={preview}
                  alt={t.landing.tabImageToVideo}
                  sx={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  size="small"
                  aria-label={t.landing.removeImage}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{
                  flexDirection: 'column',
                  justifyContent: 'center',
                  aspectRatio: '3 / 4',
                  borderStyle: 'dashed',
                  color: 'text.secondary',
                  textAlign: 'center',
                  '& .MuiButton-startIcon': { m: 0, mb: 1 },
                }}
              >
                {t.landing.uploadLabel}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {t.landing.uploadHint}
                </Typography>
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
              </Button>
            )}
          </Box>
        )}

        <Stack spacing={2} sx={{ flex: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={tab === 'image-to-video' ? 4 : 5}
            label={t.landing.promptLabel}
            placeholder={
              tab === 'image-to-video'
                ? t.landing.promptPlaceholderI2v
                : t.landing.promptPlaceholderT2v
            }
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />

          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">{t.landing.duration}</Typography>
              <Typography variant="body2">{format(t.landing.seconds, { n: duration })}</Typography>
            </Stack>
            <Slider
              value={duration}
              onChange={(_, v) => setDuration(v as number)}
              min={5}
              max={20}
              step={5}
              marks
              size="small"
            />
          </Box>

          {hint && (
            <Typography variant="body2" sx={{ color: 'warning.main' }}>
              {hint}
            </Typography>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerate}
            sx={{ alignSelf: 'flex-start', px: 4, py: 1.25 }}
          >
            {t.landing.generate}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
